import type { Extensions } from "@tiptap/core";
import { Highlight } from "@tiptap/extension-highlight";
import { TaskItem, TaskList } from "@tiptap/extension-list";
import { Subscript } from "@tiptap/extension-subscript";
import { Superscript } from "@tiptap/extension-superscript";
import { TextAlign } from "@tiptap/extension-text-align";
import { TextStyle } from "@tiptap/extension-text-style";
import { Typography } from "@tiptap/extension-typography";
import { HorizontalRule } from "src/tiptap/components/tiptap-node/horizontal-rule-node/horizontal-rule-node-extension";
import { createTableExtensions } from "src/tiptap/components/tiptap-node/table-node/table-node-extension";

/**
 * The editor content nodes/marks that a markdown string can map onto — shared by the
 * live editor (`createEditorContentExtensions` in `simple-editor.tsx`) and the headless
 * markdown renderer (`render-markdown.ts`) so a table/image/list/divider in a dynamic
 * data-key node renders through the same schema as the document.
 *
 * The `image` extension(s) are injected because the two callers need different image
 * behavior: the editor uses the upload kit (runtime upload handlers) while the renderer
 * uses a base display-only image node. Callers add their own StarterKit and any
 * interactive/provider-bound extensions (Collaboration, comments, Selection, etc.) that
 * are not part of the shared content schema.
 *
 * `HorizontalRule` is the custom divider node; callers that include StarterKit must
 * disable its built-in `horizontalRule` to avoid defining the node twice.
 */
export const createContentSchemaExtensions = ({
  image,
}: {
  image: Extensions;
}): Extensions => [
  HorizontalRule,
  TextAlign.configure({ types: ["heading", "paragraph"] }),
  TextStyle,
  TaskList,
  TaskItem.configure({ nested: true }),
  Highlight.configure({ multicolor: true }),
  Typography,
  Superscript,
  Subscript,
  ...image,
  ...createTableExtensions(),
];
